import React, { Dispatch, SetStateAction, useState } from 'react';
import { FileError, FileWithPath, useDropzone } from 'react-dropzone';
import axios from 'axios';
import CircularProgressWithLabel from '../../Components/CustomUIElements/CircularProgressWithLabel';
import { classes } from './styles';
import UploadSVG from '../Graphics/UploadSVG';
import { Box, Button, Stack } from '@mui/material';
import { theme } from '../../../ThemeContext/ThemeObject';

export default function AttachmentSelector({
  performUpload,
  uploadUrl,
  onUploadComplete,
  setFileList,
  maxSize,
  maxFiles,
  heightOverride,
  attachment,
  compact,
  maxVideoSize,
  buttonId
}: {
  performUpload: boolean;
  uploadUrl?: string;
  onUploadComplete?: () => void;
  setFileList?: Dispatch<SetStateAction<FileWithPath[]>>;
  maxSize: number;
  maxFiles?: number;
  heightOverride?: number | string;
  attachment?: boolean;
  compact?: boolean;
  maxVideoSize?: number;
  buttonId?: string;
}) {
  const getMaxSize = (fileType: string): number => {
    if (fileType.startsWith('video/')) {
      return maxVideoSize ? maxVideoSize : maxSize;
    }
    return maxSize;
  };
  const sizeValidator = (file: File): FileError | null => {
    const maximumSize = getMaxSize(file.type);
    if (file.size > maximumSize) {
      return {
        code: 'file-too-large',
        message: attachment ? 'Files must be under specified size.' : 'Files must be under 50 MB'
      };
    }
    return null;
  };
  const { getRootProps, getInputProps, open, fileRejections } = useDropzone({
    noClick: true,
    noKeyboard: true,
    validator: sizeValidator,
    maxFiles: maxFiles ? maxFiles : 0,
    onDragOver: () => {
      setIsDraggedOver(true);
    },
    onDragLeave: () => {
      setIsDraggedOver(false);
    },
    onDropRejected: () => {
      setIsRejected(true);
    },
    onDrop: (acceptedFiles: FileWithPath[]) => {
      const parsedFiles = acceptedFiles.map(
        (file) => new File([file], file.name.replace(/[()]/g, ''), { type: file.type })
      );
      if (performUpload) {
        parsedFiles.forEach((file) => {
          uploadFiles(file);
        });
      }
      if (setFileList) {
        // append new files to the existing file list
        setFileList((prev) => [...prev, ...parsedFiles]);
      }
      setIsDraggedOver(false);
      setIsRejected(false);
    }
  });

  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [percentCompleted, setPercentCompleted] = useState(0);
  const [error, setError] = useState('');

  const uploadFiles = async (file: FileWithPath) => {
    if (!uploadUrl || !onUploadComplete) {
      console.error(
        'Missing required parameters for AttachmentSelector component. If performUpload is true, please provide uploadUrl and onUploadComplete.'
      );
      return;
    }
    setIsLoading(true);
    const formData = new FormData();
    formData.append('asset', file);
    try {
      const response = await axios.post(uploadUrl, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          setPercentCompleted(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        }
      });
      if (response.status === 201 || response.status === 200) {
        setError('');
        onUploadComplete();
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        error.response.data.errors === 'Invalid file.'
          ? setError('Unsupported file type')
          : setError(error.response.data.errors);
      }
    } finally {
      setIsRejected(false);
      setIsLoading(false);
      setPercentCompleted(0);
    }
  };
  return (
    <Box sx={{ width: '100%' }}>
      <Box
        {...getRootProps()}
        sx={{
          ...classes.dragArea,
          ...(isDraggedOver ? classes.dragAreaOver : {}),
          ...(heightOverride ? { height: heightOverride } : {}),
          ...(compact ? { margin: 'unset' } : {})
        }}
      >
        <input {...getInputProps()} />
        {isLoading ? (
          <Stack
            direction={compact ? 'row' : 'column'}
            alignItems={'center'}
            spacing={2}
            sx={{ scale: compact ? '0.6' : '1.0' }}
          >
            <CircularProgressWithLabel value={percentCompleted} />
            <Box sx={{ marginTop: '12px' }}>Uploading...</Box>
          </Stack>
        ) : (
          <Stack
            direction={compact ? 'row' : 'column'}
            alignItems={'center'}
            spacing={2}
            sx={{ scale: '0.8' }}
          >
            <Box sx={{ marginBottom: '12px' }}>
              <UploadSVG />
            </Box>
            {!isDraggedOver ? (
              <>
                <Stack>
                  <Box>{compact ? 'Drag files here or' : 'Drag or choose file to upload'}</Box>
                </Stack>
                {(isRejected || error) && (
                  <Box sx={{ color: '#D6827D' }}>
                    {isRejected
                      ? attachment
                        ? 'Files must be under specified size.'
                        : 'Files must be under 50 MB'
                      : error}
                  </Box>
                )}
              </>
            ) : (
              <>
                <Box>
                  {performUpload ? 'Drop files here to upload!' : 'Drop files here to add!'}
                </Box>
                {fileRejections.length > 0 && isDraggedOver && (
                  <Box sx={{ color: '#D6827D' }}>
                    {fileRejections.map((fileRejection, index) => (
                      <Box key={index}>{fileRejection.errors.map((e) => e.message).join(', ')}</Box>
                    ))}
                  </Box>
                )}
              </>
            )}
            <Button
              id={buttonId}
              sx={classes.selectFilesButton}
              onClick={open}
              className="select-files-button"
              data-testid="select-files-button"
            >
              Choose files
            </Button>
            {attachment && (
              <Stack
                sx={{
                  fontStyle: 'italic',
                  fontWeight: 200,
                  fontSize: theme.spacing(1.5),
                  textAlign: 'center',
                  paddingTop: theme.spacing(0.5)
                }}
              >
                <Box>Maximum size per file: 50MB</Box>
                {maxVideoSize && <Box>Maximum size per video: 500MB</Box>}
              </Stack>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
}
